/**
 * AdminSalaryCoverage — per-country % of unis with a verified salary
 * source. Weakest countries surface at the top so editors know where
 * to point the next per-uni salary uplift wave.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const FLAG = {
  UK: "🇬🇧", US: "🇺🇸", Australia: "🇦🇺", Canada: "🇨🇦", France: "🇫🇷",
  Germany: "🇩🇪", Netherlands: "🇳🇱", Ireland: "🇮🇪", Italy: "🇮🇹",
  Spain: "🇪🇸", Sweden: "🇸🇪", Norway: "🇳🇴", Finland: "🇫🇮",
  Denmark: "🇩🇰", Switzerland: "🇨🇭", Austria: "🇦🇹", Belgium: "🇧🇪",
  Portugal: "🇵🇹", "Hong Kong": "🇭🇰", Singapore: "🇸🇬",
  Japan: "🇯🇵", "South Korea": "🇰🇷", India: "🇮🇳", Estonia: "🇪🇪",
  Czechia: "🇨🇿", Poland: "🇵🇱", "United Arab Emirates": "🇦🇪",
};

function pctColor(p) {
  if (p >= 75) return "bg-emerald-500";
  if (p >= 40) return "bg-amber-500";
  if (p >= 10) return "bg-orange-500";
  return "bg-red-500";
}

export default function AdminSalaryCoverage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/coverage/salary-sources")
       .then((r) => setData(r.data))
       .catch((e) => {
         if (e?.response?.status === 401 || e?.response?.status === 403) {
           setForbidden(true);
         }
       })
       .finally(() => setLoading(false));
  }, []);

  if (forbidden) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-6 text-center">
        <h1 className="font-editorial text-2xl mb-2">Admin only</h1>
        <p className="text-sm text-stone-600">This dashboard is restricted to WUA editors.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-10 px-6" data-testid="admin-salary-coverage-page">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-[11px] font-mono text-stone-500 hover:text-stone-900 uppercase tracking-wider mb-4"
      >
        <ArrowLeft className="w-3 h-3" /> Admin
      </Link>
      <header className="mb-6">
        <div className="label-uppercase text-[10px] text-stone-500 mb-1">Coverage dashboard</div>
        <h1 className="font-editorial text-3xl">Salary source coverage</h1>
        <p className="text-sm text-stone-600 mt-2 max-w-2xl">
          What share of each country's universities carries a verified per-uni salary
          figure from a recognised national statistics body — HESA, LEO, HEA, QILT,
          Scorecard, NSSE, StatCan, CBS, Céreq, INSEE, Destatis, AlmaLaurea, INE,
          DST, SCB, SSB, Tilastokeskus, AISHE/NIRF, BFS/OFS, MOHESR, Statistik AT,
          DGES, KEDI, Statbel. Countries at the top of the table are the next
          targets for a per-uni uplift wave.
        </p>
        <p className="text-[12px] text-stone-500 mt-3 max-w-2xl leading-relaxed"
           data-testid="coverage-denominator-note">
          <span className="font-underground-sc text-[10px] tracking-wider text-stone-700">Denominator note:</span>{" "}
          FE colleges, specialist studio/drama/dance private providers, non-HE
          entities, and university-centres-inside-FE-colleges are excluded from the
          denominator. They stay in the DB (HESA registers them as HE providers) but
          they aren't "universities" in the WUA sense and would otherwise dilute the
          coverage metric. Only stand-alone universities count here.
        </p>
      </header>

      {loading && !data && <div className="text-stone-500 text-sm">Loading…</div>}

      {data && (
        <>
          <section className="mb-8 flex items-baseline gap-6 p-5 rounded-lg border border-stone-200 bg-white"
                   data-testid="salary-coverage-global">
            <div>
              <div className="label-uppercase text-[10px]">Global</div>
              <div className="font-chip text-3xl font-semibold text-emerald-800">
                {data.global.percent}%
              </div>
            </div>
            <div>
              <div className="label-uppercase text-[10px]">Verified unis</div>
              <div className="font-chip text-2xl">{data.global.verified_count.toLocaleString()}</div>
            </div>
            <div>
              <div className="label-uppercase text-[10px]">Total unis</div>
              <div className="font-chip text-2xl text-stone-500">
                {data.global.total_unis.toLocaleString()}
              </div>
            </div>
          </section>

          <section>
            <h2 className="label-uppercase text-stone-700 mb-3">
              By country · sorted weakest first · {data.rows.length} countries
            </h2>
            <ul className="border border-stone-200 rounded-lg overflow-hidden bg-white divide-y divide-stone-100">
              {data.rows.map((r) => (
                <li key={r.country}
                    className="p-3 flex items-center gap-3"
                    data-testid={`salary-coverage-row-${r.country.replace(/\s+/g, "-")}`}>
                  <span className="text-lg shrink-0 w-6 text-center">{FLAG[r.country] || "🌐"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-stone-900">{r.country}</span>
                      <span className="text-[11px] font-mono text-stone-500">
                        {r.verified_count.toLocaleString()} / {r.total_unis.toLocaleString()} unis
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-stone-100 overflow-hidden">
                      <div
                        className={`h-full ${pctColor(r.percent)} transition-all`}
                        style={{ width: `${Math.max(r.percent, 1)}%` }}
                      />
                    </div>
                    {r.top_sources.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1"
                           data-testid={`salary-coverage-sources-${r.country.replace(/\s+/g, "-")}`}>
                        {r.top_sources.map((s) => (
                          <span
                            key={s.label}
                            className="text-[10px] font-underground-sc tracking-wider px-1.5 py-0.5 rounded border border-stone-300 bg-stone-50 text-stone-700"
                          >
                            {s.label} · {s.count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-lg font-semibold text-stone-900">
                      {r.percent}%
                    </div>
                    {r.percent < 25 && r.total_unis >= 10 && (
                      <div className="text-[10px] font-underground-sc tracking-wider text-red-700 mt-0.5 inline-flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> uplift target
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <p className="mt-6 text-[11px] text-stone-500 font-mono">
            Absence of a badge on a uni card = no verified per-uni salary stamp for that uni.
            The dashboard counts a uni as verified if `outcomes.salary_source` matches a
            recognised publisher regex, OR if `median_earnings_1yr_usd` is populated from US Scorecard.
          </p>
        </>
      )}
    </div>
  );
}
